import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getIssuesIndexContentQueryDeps} from '@github-ui/issue-index-query-defaults'
import {replaceDateTokens} from '@github-ui/issue-url-helper'
import {useQuery} from '@github-ui/react-query'
import {useCachePreheatTasks} from '@github-ui/relay-query-cache/hooks/use-cache-preheat-query'
import type {SidebarPinnedView} from '@github-ui/repos-issues-sidebar-ui/PinnedViewsNav'
import {RepoSavedViewsNav} from '@github-ui/repos-issues-sidebar-ui/RepoSavedViewsNav'
import {repoSidebarLinks} from '@github-ui/repos-issues-sidebar-ui/repos-issues-sidebar-links'
import {ReposIssuesSidebar} from '@github-ui/repos-issues-sidebar-ui/ReposIssuesSidebar'
import type {SavedViewsResponse} from '@github-ui/saved-views'
import {usePinnedSavedViews} from '@github-ui/saved-views/hooks/use-pinned-saved-views'
import {savedViewsQueryOptions} from '@github-ui/saved-views/queries'
import {type ReactNode, useMemo} from 'react'

import {issuesIndexQueryResolver} from '../helpers/issues-index-query-resolver'
import {issuesIndexContentRoute} from './issues-index-content-route'
import type {IssuesLayoutPayload} from './issues-layout-route'

const EMPTY_PINNED_VIEWS: SidebarPinnedView[] = []

function preheatDestination(owner: string, repo: string, query: string, savedView = false) {
  const pathname = `/${owner}/${repo}/issues`
  const searchParams = new URLSearchParams({q: savedView ? replaceDateTokens(query) : query})
  if (!savedView) searchParams.set('q', issuesIndexQueryResolver({pathname, searchParams}))

  const queryKey = issuesIndexContentRoute.queries.mainQuery.makeQueryKey(
    getIssuesIndexContentQueryDeps({pathname, query: searchParams.get('q')}),
  )
  return {
    key: JSON.stringify(queryKey),
    task: async () => issuesIndexContentRoute.preload({owner, repo}, searchParams),
  }
}

export type SidebarOptions = {
  owner: string
  repo: string
  hasProjectsEnabled: boolean
  pinnedViews: SidebarPinnedView[]
  pinnedViewsEnabled: boolean
  pinnedViewsTeamVsPersonalEnabled: boolean
}

export function renderSidebar(options: SidebarOptions) {
  return function IssuesSidebar(collapsible?: boolean): ReactNode {
    const {owner, repo, pinnedViewsEnabled, ...sidebarOptions} = options
    return (
      <ReposIssuesSidebar
        collapsible={collapsible}
        owner={owner}
        repo={repo}
        {...sidebarOptions}
        viewsEnabled={pinnedViewsEnabled}
        renderSavedViewsNav={
          pinnedViewsEnabled ? undefined : context => <RepoSavedViewsNav owner={owner} repo={repo} {...context} />
        }
      />
    )
  }
}

export function IssuesSidebarPreheater({owner, repo}: {owner: string; repo: string}) {
  const preheatingEnabled = isLoggedIn() && !isFeatureEnabled('disable_preheating_for_issues')
  const savedViewPreheatingEnabled = preheatingEnabled && isFeatureEnabled('issue_pinned_views')
  const {data: savedViews, isPlaceholderData: areSavedViewsPlaceholder} = useQuery({
    ...savedViewsQueryOptions({owner, repo}),
    enabled: savedViewPreheatingEnabled,
  })
  const preheatTasks = useMemo(
    () => [
      ...repoSidebarLinks.map(link => ({...preheatDestination(owner, repo, link.query), enabled: preheatingEnabled})),
      // During repo nav, placeholder data can still contain the previous repo's saved views
      ...(areSavedViewsPlaceholder
        ? []
        : (savedViews?.savedViews.map(view => ({
            ...preheatDestination(owner, repo, view.query, true),
            enabled: preheatingEnabled,
          })) ?? [])),
    ],
    [areSavedViewsPlaceholder, owner, preheatingEnabled, repo, savedViews?.savedViews],
  )
  useCachePreheatTasks({tasks: preheatTasks})

  return null
}

// Shared source of truth for the issues sidebar render function so the parent
// (IssuesLayout) and the leaf (Labels) — only one of which consumes it depending
// on the sidebar-layout flag — can't drift apart.
export function useRenderSidebar(
  owner: string,
  repo: string,
  layoutPayload: IssuesLayoutPayload | undefined,
  initialSavedViews?: SavedViewsResponse,
) {
  const hasProjectsEnabled = layoutPayload?.hasProjectsEnabled ?? false
  const initialPinnedViews = layoutPayload?.pinnedViews ?? EMPTY_PINNED_VIEWS
  const pinnedViewsEnabled = layoutPayload?.pinnedViewsEnabled ?? false
  const pinnedViewsTeamVsPersonalEnabled = isFeatureEnabled('issue_pinned_views_team_vs_personal')
  const {pinnedViews: savedViewPins, pinnedCount} = usePinnedSavedViews({
    owner,
    repo,
    enabled: pinnedViewsEnabled,
    initialData: initialSavedViews,
  })
  const pinnedViews = useMemo(
    () =>
      pinnedCount === undefined
        ? initialPinnedViews
        : savedViewPins.map(view => ({
            id: view.id,
            name: view.name,
            icon: view.icon,
            color: view.color,
            visibility: view.visibility,
          })),
    [initialPinnedViews, pinnedCount, savedViewPins],
  )

  return useMemo(() => {
    return renderSidebar({
      owner,
      repo,
      hasProjectsEnabled,
      pinnedViews,
      pinnedViewsEnabled,
      pinnedViewsTeamVsPersonalEnabled,
    })
  }, [owner, repo, hasProjectsEnabled, pinnedViews, pinnedViewsEnabled, pinnedViewsTeamVsPersonalEnabled])
}
